import type { ImportItem, InputFile } from "@rhema/client";
import { Button, ButtonLink, cn, DownloadIcon, FileTextIcon, PageHeader } from "@rhema/ui";
import { type DragEvent, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { UpgradeLink } from "../billing/UpgradeLink";
import { formatBytes } from "../media/prepare";
import { useSession } from "../session/session";
import { filesFromDrop, filesFromInput } from "./drop";
import { isActive, progressOf, useImportQueue, useImports } from "./ImportProvider";
import { ImportSetup } from "./ImportSetup";

const ACCEPT = ".zip,.txt,application/zip,text/plain";

function statusLine(item: ImportItem): string {
  const count = (n: number) => n.toLocaleString("en-GB");
  switch (item.status) {
    case "reading":
      return "Reading the export on this device";
    case "setup":
      return `${count(item.total)} messages from ${item.senders.length} ${item.senders.length === 1 ? "person" : "people"}. Choose who is who to start.`;
    case "queued":
      return item.done
        ? `Waiting to carry on from ${count(item.done)} of ${count(item.total)}`
        : "Waiting its turn";
    case "running":
      return `Imported ${count(item.done)} of ${count(item.total)} messages`;
    case "stopped":
      return `Stopped at ${count(item.done)} of ${count(item.total)} messages`;
    case "needs_file":
      return item.error ?? "Add this export again to carry on.";
    case "failed":
      return item.error ?? "This import stopped.";
    case "done": {
      const parts = [`Imported ${count(item.sent)} messages`];
      if (item.skipped) parts.push(`${count(item.skipped)} were already in the chat`);
      if (item.mediaMissing) parts.push(`${count(item.mediaMissing)} photos or files were not in the export`);
      return `${parts.join(". ")}.`;
    }
  }
}

function ProgressBar({ value, label }: { value: number; label: string }) {
  const percent = Math.round(value * 100);
  return (
    <span
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
      className="block h-1.5 overflow-hidden rounded-full bg-surface-header"
    >
      <span className="block h-1.5 rounded-full bg-blue" style={{ width: `${percent}%` }} />
    </span>
  );
}

function ImportRow({ item, onSetup }: { item: ImportItem; onSetup: () => void }) {
  const queue = useImportQueue();
  const again = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <li className="flex flex-col gap-2 rounded-card border border-line bg-surface p-4">
      <div className="flex items-start gap-3">
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[15px] font-semibold">{item.title}</span>
          <span className="truncate text-xs text-ink-meta">{item.name}</span>
        </span>
      </div>
      {(item.status === "running" || item.status === "queued" || item.status === "stopped") && (
        <ProgressBar value={progressOf(item)} label={`Importing ${item.title}`} />
      )}
      <p
        className={cn(
          "m-0 text-sm",
          item.status === "failed" || item.status === "needs_file" ? "text-scarlet" : "text-ink-secondary",
        )}
        role={item.status === "failed" ? "alert" : undefined}
      >
        {statusLine(item)}
      </p>
      {item.status === "queued" && item.error && <p className="m-0 text-sm text-scarlet">{item.error}</p>}
      {error && (
        <p role="alert" className="m-0 text-sm text-scarlet">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {item.status === "setup" && <Button onClick={onSetup}>Choose who is who</Button>}
        {(item.status === "running" || item.status === "queued") && (
          <Button variant="outline" size="sm" onClick={() => void queue.stop(item.id)}>
            Stop
          </Button>
        )}
        {(item.status === "stopped" || (item.status === "failed" && item.chatId)) && (
          <Button size="sm" onClick={() => void queue.resume(item.id)}>
            {item.status === "failed" ? "Try again" : "Resume"}
          </Button>
        )}
        {item.status === "needs_file" && (
          <>
            <Button size="sm" onClick={() => again.current?.click()}>
              Add the export again
            </Button>
            <input
              ref={again}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={(e) => {
                const files = filesFromInput(e.target.files);
                e.target.value = "";
                setError(null);
                void queue.reattach(item.id, files).catch((err: Error) => setError(err.message));
              }}
            />
          </>
        )}
        {item.status === "done" && item.chatId && (
          <ButtonLink href={`/chats/${item.chatId}`} size="sm" variant="tonal">
            Open the chat
          </ButtonLink>
        )}
        {item.status !== "running" && item.status !== "reading" && (
          <Button variant="ghost" size="sm" onClick={() => void queue.remove(item.id)}>
            {item.status === "done" ? "Clear" : "Remove"}
          </Button>
        )}
      </div>
    </li>
  );
}

/** The summary once nothing is left to do. */
function Summary({ items }: { items: ImportItem[] }) {
  const done = items.filter((i) => i.status === "done");
  if (!done.length || items.some(isActive)) return null;
  const sent = done.reduce((n, i) => n + i.sent, 0);
  const skipped = done.reduce((n, i) => n + i.skipped, 0);
  const files = done.reduce((n, i) => n + i.mediaUploaded, 0);
  const failed = items.filter((i) => i.status === "failed").length;
  return (
    <section aria-label="Summary" role="status" className="rounded-card bg-surface-header p-4 text-sm">
      <p className="m-0 font-semibold">
        Imported {sent.toLocaleString("en-GB")} messages into {done.length}{" "}
        {done.length === 1 ? "chat" : "chats"}.
      </p>
      <p className="m-0 mt-1 text-ink-secondary">
        {files ? `${files.toLocaleString("en-GB")} photos and files went with them. ` : ""}
        {skipped ? `${skipped.toLocaleString("en-GB")} messages were already there and were left out. ` : ""}
        {failed ? `${failed} ${failed === 1 ? "export" : "exports"} could not be imported.` : ""}
      </p>
    </section>
  );
}

/**
 * Import from WhatsApp. People add one export per chat, as many at once as they like, and each
 * goes through the queue in turn while they carry on using Rhema.
 */
export function ImportScreen() {
  const queue = useImportQueue();
  const { items, halted } = useImports();
  const { platform } = useSession();
  const files = useRef<HTMLInputElement>(null);
  const folder = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [params] = useSearchParams();
  // The share screen opens this screen with the export it just added.
  const [setupId, setSetupId] = useState<string | null>(() => params.get("setup"));
  const setupItem = items.find((i) => i.id === setupId && i.status === "setup");

  const add = (inputs: InputFile[]) => {
    if (!inputs.length) return;
    void queue.add(inputs).then((added) => {
      // With one export, go straight to choosing who is who.
      const ready = added.filter((a) => a.status === "setup");
      if (added.length === 1 && ready.length === 1) setSetupId(ready[0]!.id);
    });
  };

  // Desktop and web take exports dropped from the file manager, folders included.
  const dropZone = {
    onDragOver: (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(true);
    },
    onDragLeave: (e: DragEvent<HTMLDivElement>) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      setDragging(false);
    },
    onDrop: (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      void filesFromDrop(e.dataTransfer).then(add);
    },
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-linen">
      <div className="mx-auto w-full max-w-[640px] pb-10">
        <PageHeader
          title="Import from WhatsApp"
          size="md"
          actions={
            <ButtonLink href="/chats" variant="ghost" size="sm">
              Done
            </ButtonLink>
          }
        />
        <div className="flex flex-col gap-4 px-4">
          <p className="m-0 text-[15px] text-ink">
            In WhatsApp, open a chat, tap its name, then choose Export chat. Add the file it makes here, one
            for each chat. You can add many at once and they import one after another while you use Rhema.
          </p>
          <p className="m-0 text-sm text-ink-secondary">
            Rhema reads the export on this device. Each message is end-to-end encrypted for everyone in the
            Rhema chat, and photos and files count towards your storage.
          </p>

          {halted === "storage_full" && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-card bg-scarlet-tint p-4 text-sm text-scarlet"
            >
              <p className="m-0">
                Your Rhema storage is full, so the import stopped. Make room or upgrade your plan, then
                resume.
                <UpgradeLink />
              </p>
              <Button size="sm" className="self-start" onClick={() => void queue.resume()}>
                Resume
              </Button>
            </div>
          )}

          <div
            {...dropZone}
            className={cn(
              "flex flex-col items-center gap-3 rounded-card border-2 border-dashed px-4 py-8 text-center",
              dragging ? "border-blue bg-blue-tint" : "border-line bg-surface",
            )}
          >
            <FileTextIcon size={28} className="text-ink-secondary" />
            <p className="m-0 text-sm text-ink-secondary">
              {platform.native && platform.kind === "capacitor"
                ? "Choose the exported .zip or .txt files."
                : "Drop exported .zip or .txt files, or a folder of them, here."}
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button icon={<DownloadIcon size={18} />} onClick={() => files.current?.click()}>
                Choose files
              </Button>
              {platform.kind !== "capacitor" && (
                <Button variant="outline" onClick={() => folder.current?.click()}>
                  Choose a folder
                </Button>
              )}
            </div>
            <input
              ref={files}
              type="file"
              multiple
              accept={ACCEPT}
              hidden
              aria-label="Exported chats"
              onChange={(e) => {
                const chosen = filesFromInput(e.target.files);
                e.target.value = "";
                add(chosen);
              }}
            />
            <input
              ref={folder}
              type="file"
              hidden
              aria-label="Folder of exported chats"
              {...{ webkitdirectory: "", directory: "" }}
              onChange={(e) => {
                const chosen = filesFromInput(e.target.files);
                e.target.value = "";
                add(chosen);
              }}
            />
          </div>

          {items.length > 0 && (
            <ul aria-label="Imports" className="m-0 flex list-none flex-col gap-2 p-0">
              {items.map((item) => (
                <ImportRow key={item.id} item={item} onSetup={() => setSetupId(item.id)} />
              ))}
            </ul>
          )}
          <Summary items={items} />
          {items.some((i) => i.status === "done") && !items.some(isActive) && (
            <Button
              variant="ghost"
              size="sm"
              className="self-center"
              onClick={() => void queue.clearFinished()}
            >
              Clear finished imports
            </Button>
          )}
          <p className="m-0 text-xs text-ink-meta">
            A photo or file larger than {formatBytes(2 * 1024 ** 3)} is left out and noted in the chat.
            WhatsApp keeps up to the last 40,000 messages in an export without media, and fewer with it.
          </p>
        </div>
      </div>
      {setupItem && <ImportSetup key={setupItem.id} item={setupItem} onClose={() => setSetupId(null)} />}
    </div>
  );
}
